<template>
  <form>
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>I am available for</h1>

        <a @click="$router.go(-1)" class="btn btn-info" active-class="">
          <i class="fa fa-chevron-left"></i> Back
        </a>
      </div>
    </div>

    <div class="container-fluid">

      <div class="dashboard-block">

        <div class="row">
          <div class="col-md-10 col-lg-8" v-if="talentBudgetsHaveBeenPreLoaded">

            <div class="panel panel-grey">
              <div class="panel-body">
                <template v-if="$budgetRangeTags">
                  <h3>Budget Range</h3>

                  <tags v-if="budgetRangeTags" type="budgetRange" v-model="budgetRangeTags" :allowCreate=false :multiple="false"/>
                  <pickstar-loading v-else />
                </template>
                <ladda-button
                  type="submit"
                  class="btn btn-primary pull-right margin-t-2"
                  :click="handleSaveTagsCallback">
                  Save Changes
                </ladda-button>
              </div>
            </div>

            <div class="panel panel-grey">
              <div class="panel-body">
                <h4>In Person</h4>

                <ul class="large-checklist">
                  <profile-talent-availability-opportunity-item
                    v-if="talentBudgetsHaveBeenPreLoaded"
                    v-for="opportunity in inPersonOpportunities"
                    :budget="budget"
                    :key="opportunity.id"
                    :opportunity="opportunity"
                    :opportunities="inPersonOpportunities"
                    :budgets="talentOpportunityBudget"
                  />
                </ul>

                <h4 class="m-t-md">Online</h4>

                <ul class="large-checklist">
                  <profile-talent-availability-opportunity-item
                    v-if="talentBudgetsHaveBeenPreLoaded"
                    v-for="opportunity in onlineOpportunities"
                    :key="opportunity.id"
                    :budget="budget"
                    :opportunity="opportunity"
                    :opportunities="onlineOpportunities"
                    :budgets="talentOpportunityBudget"
                  />
                </ul>

                <h4 class="m-t-md" v-if="userCanSetEsportsOpportunities">Gaming</h4>

                <ul class="large-checklist" v-if="userCanSetEsportsOpportunities">
                  <profile-talent-availability-opportunity-item
                    v-if="talentBudgetsHaveBeenPreLoaded"
                    v-for="opportunity in esportsOpportunities"
                    :key="opportunity.id"
                    :budget="budget"
                    :opportunity="opportunity"
                    :opportunities="esportsOpportunities"
                    :budgets="talentOpportunityBudget"
                  />
                </ul>
              </div>
            </div>
            <div class="margin-20"></div>

            <ladda-button
              type="submit"
              class="btn btn-primary"
              :click="handleSaveOpportunitiesCallback">
              Save Changes
            </ladda-button>
          </div>
          <pickstar-loading v-else />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { filter } from 'lodash'
import ProfileTalentAvailabilityOpportunityItem from './ProfileTalentAvailabilityOpportunityItem'
import { notify } from 'utils'
import Tags from '../misc/Tags'
import { helpers } from 'mixins/profile'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  components: {
    ProfileTalentAvailabilityOpportunityItem,
    Tags
  },

  watch: {
    user: 'loadTags'
  },
  computed: {
    ...mapState('talent/opportunity', ['talentOpportunityBudget']),
    ...mapState('user', ['user']),
    ...mapState('talent/tag', ['tags']),
    ...mapState('opportunity', ['opportunities']),

    budgetRangeTags: {
      get () {
        return this.tags.budgetRange
      },

      set (tags) {
        this.replaceTags({ type: 'budgetRange', tags })
      }
    },

    inPersonOpportunities () {
      return filter(this.opportunities, opportunity => opportunity.appearance_type === 'in_person')
    },

    onlineOpportunities () {
      return filter(this.opportunities, opportunity => opportunity.appearance_type === 'online')
    },

    esportsOpportunities () {
      return filter(this.opportunities, opportunity => opportunity.appearance_type === 'esports')
    },

    userCanSetEsportsOpportunities () {
      if (this.$vlastMode) {
        return false
      }

      if (this.$userIsAdmin) {
        return true
      }

      if (this.$userIsManager) {
        return this.user.can_set_esports
      }

      if (this.$userIsTalent) {
        return this.user.can_set_esports
      }
      return false
    }
  },

  created: function () {
    this.loadTalentBudget()
    this.getOpportunities({level: 'all', opportunity_type: 'opportunity_type'})
    this.loadTags()
  },
  data () {
    return {
      budget: [],
      isBudgetValid: true,
      talentBudgetsHaveBeenPreLoaded: false
    }
  },
  methods: {
    ...mapActions('talent/opportunity', ['updateTalentOpportunities', 'getTalentOpportunityBudget']),
    ...mapActions('talent/tag', ['getTalentTags', 'updateTalentTags', 'replaceTags']),
    ...mapActions('opportunity', ['getOpportunities']),

    /**
     * Handle saving tags against a user.
     *
     * @return Promise
     */
    handleSaveTagsCallback () {
      return this.updateTalentTags({
        id: this.user.id,
        payload: {
          tags: {
            budgetRange: this.budgetRangeTags
          }
        }
      }).then(() => {
        notify.success('Successfully saved what you are known for.')
        this.trackEvent(this.analytics.V2.AccountSectionEditEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.AVAILABILITY })
      }).catch((errors) => {
        notify.error('Sorry something has gone wrong, please try again.')
      })
    },

    handleSaveOpportunitiesCallback () {
      if (!this.isValid()) {
        notify.error(`Some fields failed validation. Please fix them and try again`)
        return Promise.resolve()
      }
      return this.updateTalentOpportunities({
        id: this.user.id,
        payload: {
          opportunities: this.user.opportunities,
          talent_budget: this.budget
        }
      }).then(() => {
        notify.success('Successfully saved your availabilities.')
        this.trackEvent(this.analytics.V2.AccountSectionEditEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.AVAILABILITY })
      }).catch((errors) => {
        notify.error('Sorry something went wrong saving your changes, please try again.')
        throw errors
      })
    },

    isValid () {
      const pattern = /^\d+(\.\d{1,2})?$/
      let valid = true
      this.budget && this.budget.map(x => {
        if (!pattern.test(x.budget) && x.budget !== '') {
          valid = false
        }
      })
      return valid
    }
  }
}
</script>
